<template>
	<div>
		<div
			class="ecommerce-product-accessories"
			v-if="parsedProducts.length > 0"
		>
			<h2
				class="margin-bottom-xl"
				v-if="title"
			>
				{{ title }}
			</h2>
			<h3
				class="margin-bottom-xl"
				v-if="content.subtitle"
			>
				{{ content.subtitle }}
			</h3>
			<Modal v-model="showModal">
				<ECommerceProductModal
					v-bind="modalProduct"
					@add-to-cart="addToCart"
					@color-changed="colorChanged"
					@close-modal="closeModal"
				/>
			</Modal>
			<GridProductsStandalone
				:products="parsedProducts"
				:loading="loading"
				:dummies="itemsPerPage"
				:raw-products="relatedProductsWithValidPrice"
				:addToCart="true"
				quick-add
				v-if="parsedProducts.length > 0"
			>
				<template #content="{ product, rawProduct }">
					<ECommerceProductAccessoryCard
						:product="product"
						:raw-product="rawProduct"
						@show-modal="openModal"
						@add-to-cart="addToCart"
						@remove-from-cart="removeFromCart"
					/>
				</template>
			</GridProductsStandalone>
		</div>
		<div v-else-if="fetchAccessoryError">Vi møtte på en feil når vi lastet inn tilbehør</div>
		<div v-else-if="loadingInternally">
			<div class="d-flex spinner-container justify-content-center">
				<TnSpinner class="spinner" />
			</div>
		</div>
	</div>
</template>

<script>
import ecommerceIntegration from "~/integrations/ecommerce-v2-integration";
import ECommerceProductMixin from "~/components/ecommerce/ECommerceProductMixin/ECommerceProductMixin.js";
import { mapGetters, mapActions } from "vuex";
import EcommerceProductModel from "~/components/ecommerce/GridProducts/models/EcommerceProductModel";
import GizmoComponentMixin from "~/mixins/platform/GizmoComponentMixin.js";
import ShoppingCartItemModel from "~/helpers/ecommerce/ShoppingCartItemModel/ShoppingCartItemModel.js";
import { uniqBy } from "~/helpers/object-utility";
import isTestMixin from "~/mixins/platform/IsTestMixin.js";
import safeUrl from "~/helpers/ecommerce/safeEcommerceUrl";

export default defineNuxtComponent({
	name: "ECommerceProductAccessories",

	mixins: [ECommerceProductMixin, GizmoComponentMixin, isTestMixin],

	props: {
		useMonthlyPrice: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			product: {},
			relatedProducts: [],
			fetchAccessoryError: null,
			loadingInternally: false,
			width: null,
			showModal: false,
			modalProduct: ref(null),
		};
	},

	methods: {
		...mapActions("ecommerce", ["setVariantAccessories"]),
		...mapActions("checkout", ["init"]),
		EcommerceProductModel,
		async loadAccessory() {
			try {
				let relatedProducts = [];
				this.loadingInternally = true;
				if (this.content.accessories?.length > 0) {
					relatedProducts = await Promise.all(
						this.content.accessories.map((productId) => ecommerceIntegration.getProductById(productId, this.segment)),
					);
				} else if (this.rawAccessories.length > 0 && this.product.id) {
					relatedProducts = await ecommerceIntegration.getAccessoriesForProduct(this.product.id, this.segment);
				} else if (this.rawProducts.length > 0 && this.product.id) {
					relatedProducts = await ecommerceIntegration.getProductsForAccessory(this.product.id, this.segment);
				}
				if (this.variantParent) {
					// Fetch accessories connected to variant group
					const variantAccessories = await ecommerceIntegration.getAccessoriesForProduct(
						this.variantParent,
						this.segment,
					);
					for (const variant of variantAccessories) {
						relatedProducts.push(variant);
					}
					if (this.product.id) {
						this.setVariantAccessories(relatedProducts);
					}
				}
				this.relatedProducts = relatedProducts;
				this.relatedProducts = uniqBy(
					relatedProducts.filter(
						(product) => product && product.title && (!this.ignoreInventory ? product.inventory?.[0]?.amount : true),
					),
					"id",
				);
			} catch (e) {
				this.fetchAccessoryError = e;
			} finally {
				this.loadingInternally = false;
			}
		},
		closeModal() {
			this.showModal = false;
		},
		openModal(product, rawProductId) {
			this.showModal = true;
			const rawProduct = this.relatedProductsWithValidPrice?.find((item) => item?.id === rawProductId);
			product = { ...product, colorCode: rawProduct?.colorCode };
			this.modalProduct = { product, rawProduct };
		},
		colorChanged(product, rawProductId) {
			const rawProduct = this.relatedProductsWithValidPrice?.find((item) => item?.id === rawProductId);
			product = { ...product, colorCode: rawProduct?.colorCode };
			this.modalProduct = { product, rawProduct };
		},
		async addToCart(product) {
			const link = safeUrl(product?.category, product?.brand, product?.name);
			try {
				this.showModal = false;
				const metadata = {
					name: product?.name,
					price: product?.price || undefined,
					priceWithoutVat: product?.priceWithoutVat || undefined,
					salePrice: product?.salePrice || undefined,
					salePriceWithoutVat: product?.salePriceWithoutVat || undefined,
					prices: product?.prices,
					image: product?.image,
					category: product.category,
					sapProductId: product?.sapProductId,
					link: { to: link },
				};
				// allow multiple items when ordering accessory
				const id = this.onlyAccessoryProducts ? undefined : this.currentTerminal?.id;
				await this.$store.dispatch(
					"shoppingCart/add",
					ShoppingCartItemModel("hardware", { id: product?.id, quantity: 1 }, metadata, id),
				);
				if (window.shoppingCart) {
					window.shoppingCart.toggle();
				}
			} catch (err) {
				console.error(err);
			}
		},
		async removeFromCart(product) {
			try {
				const shoppingcartSubItemId = this.currentTerminal.subItems?.find((item) => item?.data?.id === product?.id)?.id;
				const shoppingcartId = this.visibleCartItems?.find((item) => item?.data?.id === product?.id)?.id;
				await this.$store.dispatch("shoppingCart/remove", shoppingcartId || shoppingcartSubItemId);
			} catch (err) {
				console.error(err);
			}
		},
		getNumberOfItems(product) {
			const getNumberOfItems = (items) => items?.filter((item) => item?.data?.id === product?.id)?.length;
			return this.onlyAccessoryProducts
				? getNumberOfItems(this.visibleCartItems)
				: getNumberOfItems(this.currentTerminal?.subItems || []);
		},
	},

	computed: {
		...mapGetters("ecommerce", ["segment"]),
		...mapGetters("checkout", ["currentTerminal", "onlyAccessoryProducts"]),
		...mapGetters("shoppingCart", ["visibleCartItems"]),
		ignoreInventory() {
			return this.isTest || false;
		},
		columnSize() {
			const products = this.relatedProducts.length;
			if (products >= 4) return 3;
			return Math.min(12 / products, 6);
		},
		variantParent() {
			return this.product.variantParentId;
		},
		itemsPerPage() {
			const mobileBreakpoint = 768;
			if (this.width < mobileBreakpoint) return 2;
			return 4;
		},
		rawAccessories() {
			return this.product?.accessories || [];
		},
		rawProducts() {
			return this.product?.accessoryFor || [];
		},
		relatedProductsWithValidPrice() {
			return this.relatedProducts
				?.filter((product) => product?.price || product?.priceWithVat)
				.sort((a, b) => a?.priority - b?.priority);
		},
		parsedProducts() {
			const parsedProducts = this.relatedProductsWithValidPrice
				.reduce((acc, product) => {
					if (!acc.some((item) => item.variantParentId && item.variantParentId === product.variantParentId)) {
						acc.push({ ...product, variants: [product] });
					} else if (acc.some((item) => item.variantParentId && item.variantParentId === product.variantParentId)) {
						const index = acc.findIndex(
							(item) => item.variantParentId && item.variantParentId === product.variantParentId,
						);
						acc[index].variants.push(product);
					}
					return acc;
				}, [])
				.filter((product) => (!this.ignoreInventory ? product?.inventory?.[0]?.amount > 0 : product))
				.sort((a, b) => a?.priority - b?.priority)
				?.map((product) => EcommerceProductModel(product, this.$store.getters["ecommerce/segment"]));
			return parsedProducts;
		},
		pages() {
			return this.relatedProducts.reduce((resultArray, item, index) => {
				const chunkIndex = Math.floor(index / this.itemsPerPage);

				if (!resultArray[chunkIndex]) {
					resultArray[chunkIndex] = [];
				}

				resultArray[chunkIndex].push(item);

				return resultArray;
			}, []);
		},
		title() {
			if (this.content.title) {
				return this.content.title;
			} else if (this.relatedProductsWithValidPrice.length > 0) {
				return `Anbefalt tilbehør til ${this.product.cleanName}`;
			} else if (this.rawProducts.length > 0) {
				return `Passer til ${this.product.cleanName}`;
			}
			return "Anbefalt tilbehør";
		},
		loading() {
			return this.loadingInternally || this.$store.getters["checkout/loading"];
		},
	},

	async mounted() {
		if (!this.content.useProductFromState) {
			this.product = await ecommerceIntegration.getProductById(this.currentTerminal?.data?.id, this.segment);
		} else {
			this.product = this.eCommerceProduct;
		}
	},

	watch: {
		product: {
			handler(value) {
				if (value?.id && !this.loadingInternally && !this.relatedProducts?.length && !this.fetchAccessoryError) {
					this.loadAccessory();
				}
			},
			immediate: true,
		},
	},

	beforeMount() {
		if (import.meta.browser) {
			this.width = window?.innerWidth;
		}
	},
});
</script>

<style lang="scss" scoped>
$small-card-width: 278px;
$medium-card-width: 372px;
$large-card-width: 570px;

h2 {
	text-align: center;

	@include font-title-m;
}

h3 {
	text-align: center;

	@include font-text-l;
}

.ecommerce-product-accessories :deep(.carousel-slide) {
	align-items: flex-start;
}

.carousel {
	width: 100%;
	overflow: hidden;
}

.products {
	display: grid;
	grid-gap: 48px 24px;
	grid-template-columns: 1fr;

	// Desktop
	@media screen and (min-width: $size-screen-tablet) {
		&.sm {
			grid-template-columns: repeat(auto-fill, $small-card-width);
		}

		&.md {
			grid-template-columns: repeat(auto-fill, $medium-card-width);
		}

		&.xl {
			grid-template-columns: repeat(auto-fill, $large-card-width);
		}
	}

	// Tablet
	@include breakpoint(tablet) {
		&.sm,
		&.md,
		&.xl {
			grid-template-columns: 1fr 1fr;
		}
	}

	// Phone
	@include breakpoint(mobile) {
		&.sm {
			grid-template-columns: 1fr 1fr;
		}

		&.md,
		&.xl {
			grid-template-columns: 1fr;
		}
	}
}

.spinner-container {
	align-items: center;
	height: 400px;
}
</style>
